import { formatWafTypeDisplay } from "./wafSourceLabels";

const RAW_EVENT_EXCLUDED_KEYS = new Set([
  "raw",
  "log_type",
  "match_messages",
  "matched_rules",
  "rule_details",
  "sd_node_type",
  "secRuleIds",
  "sg_waf_id",
  "total_latency_ms",
  "sp_attack_types",
  "sp_attacks",
  "sp_confidence",
  "sp_correlation",
  "sp_explanation",
  "sp_intent_tag_summary",
  "sp_intent_tags",
  "sp_is_attack",
  "sp_recommendations",
  "sp_risk_level",
  "sp_summary",
  "sp_verdict",
  "timestamp",
  "transaction_id",
  "type",
  "waf_table",
  "country_iso_code",
  "country_name",
  "categories_detected",
  "attack_types",
]);

const emptyValue = "(Empty)";

const safeText = (value, fallback = emptyValue) => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
};

const dedupeStrings = (items) =>
  [...new Set((Array.isArray(items) ? items : []).filter((item) => typeof item === "string" && item.trim()))];

/**
 * Normalize remediation-style fields: string[], JSON string array, embedded `[...]`,
 * or a single line like `1. ["a","b"]` from upstream serialization.
 */
const coerceToStringList = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((v) => coerceToStringList(v));
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return [String(value)];
  }
  if (typeof value !== "string") return [];

  let s = value.trim();
  if (!s) return [];

  const stripNumberPrefix = (line) => line.replace(/^\s*\d+[.)]\s*/, "").trim();

  const tryParseJson = (str) => {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed.flatMap((x) => coerceToStringList(x));
      if (typeof parsed === "string") return coerceToStringList(parsed);
    } catch (_err) {
      /* ignore */
    }
    return undefined;
  };

  const fromFull = tryParseJson(s);
  if (fromFull !== undefined) return fromFull;

  const stripped = stripNumberPrefix(s);
  if (stripped !== s) {
    const fromStripped = tryParseJson(stripped);
    if (fromStripped !== undefined) return fromStripped;
  }

  const bracket = s.indexOf("[");
  const bracketEnd = s.lastIndexOf("]");
  if (bracket !== -1 && bracketEnd > bracket) {
    const slice = s.slice(bracket, bracketEnd + 1);
    const embedded = tryParseJson(slice);
    if (embedded !== undefined && embedded.length > 0) return embedded;
  }

  const lines = s.split(/\r?\n/).map((line) => stripNumberPrefix(line)).filter(Boolean);
  if (lines.length > 1) return lines;

  const oneLine = stripNumberPrefix(s);
  if (oneLine && oneLine !== s) return [oneLine];

  return [s];
};

const parseArrayFromJsonString = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
};

const parseRawHeaders = (rawHeaders) => {
  if (typeof rawHeaders !== "string" || !rawHeaders.trim()) return [];
  return rawHeaders
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, idx) => {
      const sepIndex = line.indexOf(":");
      if (sepIndex === -1) return { key: idx === 0 ? "Request Line" : `Line ${idx + 1}`, value: line };
      return {
        key: line.slice(0, sepIndex).trim(),
        value: line.slice(sepIndex + 1).trim(),
      };
    });
};

const REQUEST_DETAILS_HEADER_KEYS = new Set([
  "host",
  "priority",
  "user-agent",
  "x-ja3-hash",
  "ja3",
  "ja3-hash",
  "method",
  "path",
  "request-uri",
  "request_uri",
  "http-protocol",
  "http_protocol",
  "protocol",
]);

const sanitizeCookieHeader = (cookieValue) => {
  if (typeof cookieValue !== "string" || !cookieValue.trim()) return "";
  return cookieValue
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      const key = part.split("=")[0]?.trim()?.toLowerCase() || "";
      return !(key.startsWith("__sd") || key.startsWith("__ld"));
    })
    .join("; ");
};

const sanitizeRequestHeaders = (headers) =>
  (Array.isArray(headers) ? headers : [])
    .map((header) => ({
      key: String(header?.key || "").trim(),
      value: String(header?.value || "").trim(),
    }))
    .filter((header) => header.key && header.value)
    .filter((header) => {
      const normalizedKey = header.key.toLowerCase();
      if (normalizedKey === "request line") return true;
      return !REQUEST_DETAILS_HEADER_KEYS.has(normalizedKey);
    })
    .map((header) => {
      const normalizedKey = header.key.toLowerCase();
      if (normalizedKey === "cookie") {
        const sanitizedCookie = sanitizeCookieHeader(header.value);
        return { ...header, value: sanitizedCookie || "(Filtered)" };
      }
      return header;
    });

/** Smart Parse drawer — omit fields duplicated in structured sections. */
const omitKeysDeep = (value, excludedKeys = RAW_EVENT_EXCLUDED_KEYS) => {
  if (Array.isArray(value)) {
    return value.map((item) => omitKeysDeep(item, excludedKeys));
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((acc, [key, entryValue]) => {
    if (excludedKeys.has(key)) return acc;
    acc[key] = omitKeysDeep(entryValue, excludedKeys);
    return acc;
  }, {});
};

/** Signature WAF drawer — keep attack_types, matched_rules, match_* from the API row. */
const SIGNATURE_WAF_RAW_EVENT_EXCLUDED_KEYS = new Set(
  [...RAW_EVENT_EXCLUDED_KEYS].filter(
    (key) => !["attack_types", "matched_rules", "matched_rule_id"].includes(key)
  )
);

const filterSignatureWafRawEventForDrawer = (value) =>
  omitKeysDeep(value, SIGNATURE_WAF_RAW_EVENT_EXCLUDED_KEYS);

const getDetectedIntents = (eventData) => {
  const direct = Array.isArray(eventData.detected_intents) ? eventData.detected_intents : [];
  if (direct.length > 0) return direct;

  /** Canonical single-event API: `intent_tags` is `{ tags: [ full intent objects with signals ] }` */
  const envelope = eventData.intent_tags;
  if (envelope && typeof envelope === "object" && !Array.isArray(envelope) && Array.isArray(envelope.tags)) {
    return envelope.tags;
  }

  const fromIntentTags = parseArrayFromJsonString(eventData.intent_tags);
  if (fromIntentTags.length > 0) return fromIntentTags;

  return [];
};

const sanitizeSignalMatchedValue = (v) => {
  if (v == null) return "";
  return String(v).replace(/\u0000/g, " · ").trim();
};

const normalizeIntentTagSignalRow = (s) => {
  if (!s || typeof s !== "object") return null;
  const signalStr = s.signal != null ? String(s.signal).trim() : "";
  const explanationStr = s.explanation != null ? String(s.explanation).trim() : "";
  const valueStr = sanitizeSignalMatchedValue(s.matched_value ?? s.signal_value ?? s.value);
  if (!signalStr && !explanationStr && !valueStr) return null;
  return {
    signal: signalStr,
    signal_value: valueStr,
    explanation: explanationStr,
    severity: (s.severity != null ? String(s.severity).trim().toLowerCase() : "") || "",
  };
};

const normalizeIntentTagEntry = (raw, idx) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    return {
      id: "",
      name: t,
      severity_label: "",
      category: "",
      description: "",
      signals: [],
    };
  }
  if (typeof raw !== "object") return null;
  const nameRaw = raw.name ?? raw.intent_name ?? raw.tag ?? raw.label ?? raw.intent;
  const name = typeof nameRaw === "string" ? nameRaw.trim() : nameRaw != null ? String(nameRaw).trim() : "";
  if (!name) return null;
  const classification = raw.classification && typeof raw.classification === "object" ? raw.classification : {};
  const signals = Array.isArray(raw.signals)
    ? raw.signals.map(normalizeIntentTagSignalRow).filter(Boolean)
    : [];
  return {
    id: safeText(raw.id ?? raw.intent_id ?? raw.tag_id ?? `tag-${idx}`, ""),
    name,
    severity_label: safeText(
      raw.severity_label ?? raw.severity ?? raw.risk ?? classification.severity,
      ""
    ),
    category: safeText(raw.category ?? raw.type ?? classification.owasp_category ?? classification.cwe_id, ""),
    description: safeText(raw.description ?? classification.description, ""),
    matched: raw.matched,
    mitre: raw.mitre ?? raw.mitre_id,
    cwe: raw.cwe ?? raw.cwe_id,
    signals,
  };
};

const parseIntentTagsPayload = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((item, i) => normalizeIntentTagEntry(item, i)).filter(Boolean);
  }
  if (typeof raw === "object" && Array.isArray(raw.tags)) {
    return parseIntentTagsPayload(raw.tags);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      return parseIntentTagsPayload(parsed);
    } catch (_err) {
      const one = normalizeIntentTagEntry(t, 0);
      return one ? [one] : [];
    }
  }
  return [];
};

const firstNonEmptyIntentTags = (...sources) => {
  for (const source of sources) {
    const parsed = parseIntentTagsPayload(source);
    if (parsed.length > 0) return parsed;
  }
  return [];
};

const normalizeIntent = (intent) => {
  const classification = intent.classification || {};
  return {
    intent_id: safeText(intent.intent_id),
    intent_name: safeText(intent.intent_name || intent.intent),
    classification: safeText(classification.label || classification.type || "Threat Classification"),
    description: safeText(intent.description || classification.description),
    severity: safeText(classification.severity || intent.severity),
    attacker_objective: safeText(intent.attacker_objective || intent.objective),
    verified: intent?.verified,
    cwe_id: safeText(classification.cwe_id || intent.cwe_id),
    cwe_name: safeText(classification.cwe_name || intent.cwe_name),
    mitre_id: safeText(classification.mitre_id || intent.mitre_id),
    mitre_tactic: safeText(classification.mitre_tactic || intent.mitre_tactic),
    owasp_category: safeText(classification.owasp_category || intent.owasp_category),
    classification_label: safeText(classification.label || classification.type || intent.classification_name || "Threat Classification"),
    mapping: {
      cwe: safeText(classification.cwe_id || intent.cwe_id),
      mitre: safeText(classification.mitre_id || intent.mitre_id),
      owasp: safeText(classification.owasp_category || intent.owasp_category),
    },
    signals: Array.isArray(intent.signals) ? intent.signals : [],
    recommendations: dedupeStrings([
      ...coerceToStringList(intent.remediation),
      ...coerceToStringList(intent.recommendations),
      ...coerceToStringList(classification.recommendations),
      ...coerceToStringList(classification.remediation),
    ]),
  };
};

const normalizeSignal = (signal) => ({
  signal: safeText(signal.signal || signal.type),
  signal_value: safeText(signal.signal_value || signal.value || signal.matched_value),
  explanation: safeText(signal.explanation || signal.description),
  severity: safeText(signal.severity || signal.risk_level),
});

const formatCampaignAge = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1) return `${n.toFixed(1)} s`;
  if (n < 60) return `${Math.round(n)} s`;
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}m ${s}s`;
};

const confidenceToPct = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n >= 0 && n <= 1) return Math.round(n * 100);
  if (n > 1 && n <= 100) return Math.round(n);
  return Math.min(100, Math.round(n));
};

const sanitizePayloadPreview = (value) => {
  if (value == null) return "";
  const s = String(value).replace(/\u0000/g, " · ").trim();
  return s;
};

const parseCampaignObject = (c) => {
  if (!c || typeof c !== "object" || Array.isArray(c)) return null;
  const killRaw = c.kill_chain;
  const killChain = Array.isArray(killRaw)
    ? killRaw.map((k) => {
        if (!k || typeof k !== "object") return null;
        return {
          stage: safeText(k.stage, ""),
          verdict: safeText(k.verdict, ""),
          intent: safeText(k.intent, ""),
          timestamp: safeText(k.timestamp, ""),
          confidencePct: confidenceToPct(k.confidence),
          payloadPreview: sanitizePayloadPreview(k.payload_preview),
        };
      }).filter(Boolean)
    : [];
  const uniqueIntentTypes = Array.isArray(c.unique_intent_types)
    ? c.unique_intent_types.map((x) => (x != null ? String(x).trim() : "")).filter(Boolean)
    : [];
  const ageSeconds = c.campaign_age_seconds;
  const detectionCount =
    c.detection_count != null && c.detection_count !== "" && Number.isFinite(Number(c.detection_count))
      ? Number(c.detection_count)
      : null;
  const progressionRaw = c.progression;
  const progression =
    progressionRaw == null || progressionRaw === ""
      ? ""
      : typeof progressionRaw === "string"
        ? progressionRaw.trim()
        : String(progressionRaw).trim();

  return {
    campaignAgeSeconds: Number.isFinite(Number(ageSeconds)) ? Number(ageSeconds) : null,
    campaignAgeLabel: formatCampaignAge(ageSeconds),
    detectionCount,
    isEscalating: Boolean(c.is_escalating),
    isMultiVector: Boolean(c.is_multi_vector),
    progression: progression || "",
    uniqueIntentTypes,
    killChain,
  };
};

const isCampaignDrawerEmpty = (camp) => {
  if (!camp) return true;
  if (camp.killChain.length > 0) return false;
  if (camp.uniqueIntentTypes.length > 0) return false;
  if (camp.detectionCount != null && camp.detectionCount > 0) return false;
  if (camp.campaignAgeSeconds != null && Number.isFinite(camp.campaignAgeSeconds)) return false;
  if (camp.isEscalating || camp.isMultiVector) return false;
  const prog = (camp.progression || "").trim().toLowerCase();
  if (prog && prog !== "none") return false;
  return true;
};

/**
 * Smart Parse campaign blob: object or JSON string from API / raw row.
 * Returns a normalized object for the drawer, or null when absent or non-informative.
 */
export const parseCampaignForDrawer = (raw) => {
  if (raw == null) return null;
  let obj = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t || t === "{}" || t === "null") return null;
    try {
      obj = JSON.parse(t);
    } catch (_e) {
      return null;
    }
  }
  const camp = parseCampaignObject(obj);
  if (!camp || isCampaignDrawerEmpty(camp)) return null;
  return camp;
};

/** Pick list row vs fetched detail for Smart Parse drawer (match request_id or id). */
export const resolveSmartParseDrawerEvent = (event, smartParseEvent) => {
  if (!smartParseEvent) return event || {};
  const listKey = event?.request_id || event?.id;
  const detailKey = smartParseEvent.request_id || smartParseEvent.id;
  if (!listKey || !detailKey) {
    return detailKey ? smartParseEvent : event || {};
  }
  if (detailKey === listKey) return smartParseEvent;
  if (smartParseEvent.id === event?.id && event?.id) return smartParseEvent;
  return event || {};
};

export const buildEventDrawerData = ({ event, eventType, smartParseEvent }) => {
  const isSmartParseEvent = eventType === "smart_parse";
  const eventData = isSmartParseEvent
    ? resolveSmartParseDrawerEvent(event, smartParseEvent)
    : (event || {});
  const headersFromObject =
    eventData?.request?.headers && typeof eventData.request.headers === "object"
      ? Object.entries(eventData.request.headers).map(([key, value]) => ({ key, value: String(value) }))
      : [];
  const requestHeadersRaw =
    headersFromObject.length > 0 ? headersFromObject : parseRawHeaders(eventData.request_headers || eventData.request?.headers_raw || "");
  const requestHeaders = sanitizeRequestHeaders(requestHeadersRaw);
  const responseHeaders =
    eventData.response_headers && typeof eventData.response_headers === "object"
      ? Object.entries(eventData.response_headers).map(([key, value]) => ({ key, value: String(value) }))
      : [];

  const intents = getDetectedIntents(eventData).map(normalizeIntent);
  const intentSignals = intents.flatMap((intent) => intent.signals.map(normalizeSignal));
  const attackSignalsFromAttacks = (Array.isArray(eventData.attacks) ? eventData.attacks : []).flatMap((attack) =>
    (Array.isArray(attack.evidence) ? attack.evidence : []).map((ev) =>
      normalizeSignal({
        signal: ev.scope || ev.type || "Signal",
        signal_value: ev.pattern || ev.attack_signals || ev.matched,
        explanation: ev.description || ev.reason,
        severity: ev.severity || attack.severity || attack.risk_level,
      })
    )
  );
  const attackSignals = [...intentSignals, ...attackSignalsFromAttacks];

  const requestUri = eventData.request_uri || eventData.uri || eventData.path || emptyValue;
  const [pathWithoutQuery] = String(requestUri).split("?");
  const requestBody = eventData.request_body || eventData.request?.body || eventData.body || "";

  const recommendationsFromEvent = dedupeStrings([
    ...coerceToStringList(eventData.remediation),
    ...coerceToStringList(eventData.sp_recommendations),
  ]);

  const intentTagsRich = firstNonEmptyIntentTags(eventData.intent_tags, eventData.detected_intents);
  // Single-event (canonical) fetch often omits campaign; list row from POST still has it — merge so the drawer stays stable.
  const campaignRaw = eventData.campaign || undefined;
  const campaign = parseCampaignForDrawer(campaignRaw);
  const recommendationsFromIntent = dedupeStrings(intents.flatMap((intent) => intent.recommendations));
  // const ipReputation = eventData.raw?.ip_reputation || {};
  const ipReputation = eventData.ip_reputation || eventData.abuseip || eventData.network?.ip_reputation || {};
  const rawAbuseScore = ipReputation.abuseConfidenceScore;
  const abuseConfidenceScore = Number.isFinite(Number(rawAbuseScore))
    ? Number(rawAbuseScore)
    : null;
  const remediationByIntent = intents
    .map((intent) => ({
      intentId: intent.intent_id,
      intentName: intent.intent_name,
      recommendations: dedupeStrings(intent.recommendations),
    }))
    .filter((group) => group.recommendations.length > 0);

  return {
    detectionSummary: {
      verdict: safeText(eventData.verdict),
      riskLevel: safeText(eventData.risk_level),
      confidence: safeText(eventData.confidence || "0"),
      action: safeText(eventData.action || eventData.action_taken || eventData.waf_action),
      wafType: formatWafTypeDisplay(eventData.waf_type || eventData.source_table || eventData._src, safeText(eventData.waf_type || eventData.source_table)),
      detectedIntents: intents.map((intent) => intent.intent_name).filter((name) => name !== "—"),
    },
    requestDetails: {
      host: safeText(eventData.host_name || eventData.host || eventData.request?.host),
      method: safeText(eventData.method),
      requestedUri: safeText(requestUri),
      path: safeText(pathWithoutQuery),
      httpProtocol: safeText(eventData.incoming_protocol || eventData.server_protocol || eventData.request?.protocol || eventData.request?.http_version),
      userAgent: safeText(eventData.ua || eventData.user_agent || eventData.user_agent_original || eventData.request?.headers?.["User-Agent"] || eventData.request?.headers?.user_agent),
      requestHeaders,
      ja3Hash: safeText(eventData.ja3_hash || eventData.ja3 || eventData.ja3_fingerprint),
      requestBody: requestBody && String(requestBody).trim() ? String(requestBody) : "(Empty)",
    },
    responseDetails: {
      statusCode: safeText(eventData.res_status || eventData.resStatus || eventData.status || eventData.res_code),
      responseHeaders,
    },
    networkInformation: {
      sourceIp: safeText(eventData.network?.source_ip || eventData.src_ip || eventData.ip),
      sourcePort: safeText(eventData.network?.source_port),
      ja3Fingerprint: safeText(eventData.ja3_hash || eventData.ja3 || eventData.ja3_fingerprint),
      country: safeText(eventData.network?.geo?.country || eventData.country_name || eventData.country),
      asn: safeText(eventData.network?.asn?.number || eventData.asn),
      asnOrg: safeText(eventData.asn_org),
      city: safeText(eventData.network?.geo?.city || eventData.city),
      region: safeText(eventData.network?.geo?.region || eventData.region),
      timezone: safeText(eventData.network?.geo?.timezone || eventData.timezone),
    },
    threatIntel: {
      ipAddress: safeText(ipReputation.ipAddress),
      abuseConfidenceScore,
      totalReports: ipReputation.totalReports || 0,
      isp: safeText(ipReputation.isp),
      usageType: safeText(ipReputation.usageType),
      countryCode: safeText(ipReputation.countryCode),
    },
    intentIntelligence: intents,
    attackSignals,
    remediationByIntent,
    remediationRecommendations: dedupeStrings([...recommendationsFromEvent, ...recommendationsFromIntent]),
    intentTagsRich,
    campaign,
    rawEventFiltered: omitKeysDeep(eventData),
    similarEventFilters: {
      sourceIp: safeText(eventData.src_ip || eventData.source_address, ""),
      detectionType: safeText(
        (Array.isArray(eventData.attack_types) && eventData.attack_types[0]) ||
          (Array.isArray(eventData.type) && eventData.type[0]?.attack_type) ||
          (Array.isArray(eventData.type) && eventData.type[0]),
        ""
      ),
      country: safeText(eventData.country_name || eventData.country_iso_code, ""),
      status: safeText(eventData.action || eventData.verdict, ""),
    },
  };
};

export {
  RAW_EVENT_EXCLUDED_KEYS,
  SIGNATURE_WAF_RAW_EVENT_EXCLUDED_KEYS,
  omitKeysDeep as filterRawEventForDrawer,
  filterSignatureWafRawEventForDrawer,
};
